/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `accelerometer_data` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class AccelerometerDataMesg : Mesg {
    public constructor() : super("AccelerometerData", Profile.MesgNum.ACCELEROMETER_DATA)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. Whole second part of the timestamp */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** timestamp_ms, in ms. Millisecond part of the timestamp. */
    public var timestampMs: UShort?
        get() = getFieldValue(TIMESTAMP_MS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TIMESTAMP_MS_FIELD_NUM, value)

    /** Number of values stored in sample_time_offset. */
    public val numSampleTimeOffset: Int
        get() = getField(SAMPLE_TIME_OFFSET_FIELD_NUM)?.numValues ?: 0

    /** sample_time_offset, in ms. Each time in the array describes the time at which the accelerometer sample with the corrosponding index was taken. Limited to 30 samples in each message. The samples may span across seconds. Array size must match the number of samples in accel_x and accel_y and accel_z */
    public var sampleTimeOffset: List<UShort?>?
        get() {
            val field = getField(SAMPLE_TIME_OFFSET_FIELD_NUM) ?: return null
            return List(field.numValues) { getSampleTimeOffset(it) }
        }
        set(value) {
            removeField(SAMPLE_TIME_OFFSET_FIELD_NUM)
            value?.forEachIndexed { index, element -> setSampleTimeOffset(index, element) }
        }

    public fun getSampleTimeOffset(index: Int): UShort? =
        getFieldValue(SAMPLE_TIME_OFFSET_FIELD_NUM, index) as? UShort

    public fun setSampleTimeOffset(index: Int, value: UShort?) {
        setFieldValue(SAMPLE_TIME_OFFSET_FIELD_NUM, value, index)
    }

    /** Number of values stored in accel_x. */
    public val numAccelX: Int
        get() = getField(ACCEL_X_FIELD_NUM)?.numValues ?: 0

    /** accel_x, in counts. These are the raw ADC reading. Maximum number of samples is 30 in each message. The samples may span across seconds. A conversion will need to be done on this data once read. */
    public var accelX: List<UShort?>?
        get() {
            val field = getField(ACCEL_X_FIELD_NUM) ?: return null
            return List(field.numValues) { getAccelX(it) }
        }
        set(value) {
            removeField(ACCEL_X_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAccelX(index, element) }
        }

    public fun getAccelX(index: Int): UShort? =
        getFieldValue(ACCEL_X_FIELD_NUM, index) as? UShort

    public fun setAccelX(index: Int, value: UShort?) {
        setFieldValue(ACCEL_X_FIELD_NUM, value, index)
    }

    /** Number of values stored in accel_y. */
    public val numAccelY: Int
        get() = getField(ACCEL_Y_FIELD_NUM)?.numValues ?: 0

    /** accel_y, in counts. These are the raw ADC reading. Maximum number of samples is 30 in each message. The samples may span across seconds. A conversion will need to be done on this data once read. */
    public var accelY: List<UShort?>?
        get() {
            val field = getField(ACCEL_Y_FIELD_NUM) ?: return null
            return List(field.numValues) { getAccelY(it) }
        }
        set(value) {
            removeField(ACCEL_Y_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAccelY(index, element) }
        }

    public fun getAccelY(index: Int): UShort? =
        getFieldValue(ACCEL_Y_FIELD_NUM, index) as? UShort

    public fun setAccelY(index: Int, value: UShort?) {
        setFieldValue(ACCEL_Y_FIELD_NUM, value, index)
    }

    /** Number of values stored in accel_z. */
    public val numAccelZ: Int
        get() = getField(ACCEL_Z_FIELD_NUM)?.numValues ?: 0

    /** accel_z, in counts. These are the raw ADC reading. Maximum number of samples is 30 in each message. The samples may span across seconds. A conversion will need to be done on this data once read. */
    public var accelZ: List<UShort?>?
        get() {
            val field = getField(ACCEL_Z_FIELD_NUM) ?: return null
            return List(field.numValues) { getAccelZ(it) }
        }
        set(value) {
            removeField(ACCEL_Z_FIELD_NUM)
            value?.forEachIndexed { index, element -> setAccelZ(index, element) }
        }

    public fun getAccelZ(index: Int): UShort? =
        getFieldValue(ACCEL_Z_FIELD_NUM, index) as? UShort

    public fun setAccelZ(index: Int, value: UShort?) {
        setFieldValue(ACCEL_Z_FIELD_NUM, value, index)
    }

    /** Number of values stored in calibrated_accel_x. */
    public val numCalibratedAccelX: Int
        get() = getField(CALIBRATED_ACCEL_X_FIELD_NUM)?.numValues ?: 0

    /** calibrated_accel_x, in g. Calibrated accel reading */
    public var calibratedAccelX: List<Float?>?
        get() {
            val field = getField(CALIBRATED_ACCEL_X_FIELD_NUM) ?: return null
            return List(field.numValues) { getCalibratedAccelX(it) }
        }
        set(value) {
            removeField(CALIBRATED_ACCEL_X_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCalibratedAccelX(index, element) }
        }

    public fun getCalibratedAccelX(index: Int): Float? =
        getFieldValue(CALIBRATED_ACCEL_X_FIELD_NUM, index) as? Float

    public fun setCalibratedAccelX(index: Int, value: Float?) {
        setFieldValue(CALIBRATED_ACCEL_X_FIELD_NUM, value, index)
    }

    /** Number of values stored in calibrated_accel_y. */
    public val numCalibratedAccelY: Int
        get() = getField(CALIBRATED_ACCEL_Y_FIELD_NUM)?.numValues ?: 0

    /** calibrated_accel_y, in g. Calibrated accel reading */
    public var calibratedAccelY: List<Float?>?
        get() {
            val field = getField(CALIBRATED_ACCEL_Y_FIELD_NUM) ?: return null
            return List(field.numValues) { getCalibratedAccelY(it) }
        }
        set(value) {
            removeField(CALIBRATED_ACCEL_Y_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCalibratedAccelY(index, element) }
        }

    public fun getCalibratedAccelY(index: Int): Float? =
        getFieldValue(CALIBRATED_ACCEL_Y_FIELD_NUM, index) as? Float

    public fun setCalibratedAccelY(index: Int, value: Float?) {
        setFieldValue(CALIBRATED_ACCEL_Y_FIELD_NUM, value, index)
    }

    /** Number of values stored in calibrated_accel_z. */
    public val numCalibratedAccelZ: Int
        get() = getField(CALIBRATED_ACCEL_Z_FIELD_NUM)?.numValues ?: 0

    /** calibrated_accel_z, in g. Calibrated accel reading */
    public var calibratedAccelZ: List<Float?>?
        get() {
            val field = getField(CALIBRATED_ACCEL_Z_FIELD_NUM) ?: return null
            return List(field.numValues) { getCalibratedAccelZ(it) }
        }
        set(value) {
            removeField(CALIBRATED_ACCEL_Z_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCalibratedAccelZ(index, element) }
        }

    public fun getCalibratedAccelZ(index: Int): Float? =
        getFieldValue(CALIBRATED_ACCEL_Z_FIELD_NUM, index) as? Float

    public fun setCalibratedAccelZ(index: Int, value: Float?) {
        setFieldValue(CALIBRATED_ACCEL_Z_FIELD_NUM, value, index)
    }

    /** Number of values stored in compressed_calibrated_accel_x. */
    public val numCompressedCalibratedAccelX: Int
        get() = getField(COMPRESSED_CALIBRATED_ACCEL_X_FIELD_NUM)?.numValues ?: 0

    /** compressed_calibrated_accel_x, in mG. Calibrated accel reading */
    public var compressedCalibratedAccelX: List<Short?>?
        get() {
            val field = getField(COMPRESSED_CALIBRATED_ACCEL_X_FIELD_NUM) ?: return null
            return List(field.numValues) { getCompressedCalibratedAccelX(it) }
        }
        set(value) {
            removeField(COMPRESSED_CALIBRATED_ACCEL_X_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCompressedCalibratedAccelX(index, element) }
        }

    public fun getCompressedCalibratedAccelX(index: Int): Short? =
        getFieldValue(COMPRESSED_CALIBRATED_ACCEL_X_FIELD_NUM, index) as? Short

    public fun setCompressedCalibratedAccelX(index: Int, value: Short?) {
        setFieldValue(COMPRESSED_CALIBRATED_ACCEL_X_FIELD_NUM, value, index)
    }

    /** Number of values stored in compressed_calibrated_accel_y. */
    public val numCompressedCalibratedAccelY: Int
        get() = getField(COMPRESSED_CALIBRATED_ACCEL_Y_FIELD_NUM)?.numValues ?: 0

    /** compressed_calibrated_accel_y, in mG. Calibrated accel reading */
    public var compressedCalibratedAccelY: List<Short?>?
        get() {
            val field = getField(COMPRESSED_CALIBRATED_ACCEL_Y_FIELD_NUM) ?: return null
            return List(field.numValues) { getCompressedCalibratedAccelY(it) }
        }
        set(value) {
            removeField(COMPRESSED_CALIBRATED_ACCEL_Y_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCompressedCalibratedAccelY(index, element) }
        }

    public fun getCompressedCalibratedAccelY(index: Int): Short? =
        getFieldValue(COMPRESSED_CALIBRATED_ACCEL_Y_FIELD_NUM, index) as? Short

    public fun setCompressedCalibratedAccelY(index: Int, value: Short?) {
        setFieldValue(COMPRESSED_CALIBRATED_ACCEL_Y_FIELD_NUM, value, index)
    }

    /** Number of values stored in compressed_calibrated_accel_z. */
    public val numCompressedCalibratedAccelZ: Int
        get() = getField(COMPRESSED_CALIBRATED_ACCEL_Z_FIELD_NUM)?.numValues ?: 0

    /** compressed_calibrated_accel_z, in mG. Calibrated accel reading */
    public var compressedCalibratedAccelZ: List<Short?>?
        get() {
            val field = getField(COMPRESSED_CALIBRATED_ACCEL_Z_FIELD_NUM) ?: return null
            return List(field.numValues) { getCompressedCalibratedAccelZ(it) }
        }
        set(value) {
            removeField(COMPRESSED_CALIBRATED_ACCEL_Z_FIELD_NUM)
            value?.forEachIndexed { index, element -> setCompressedCalibratedAccelZ(index, element) }
        }

    public fun getCompressedCalibratedAccelZ(index: Int): Short? =
        getFieldValue(COMPRESSED_CALIBRATED_ACCEL_Z_FIELD_NUM, index) as? Short

    public fun setCompressedCalibratedAccelZ(index: Int, value: Short?) {
        setFieldValue(COMPRESSED_CALIBRATED_ACCEL_Z_FIELD_NUM, value, index)
    }

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val TIMESTAMP_MS_FIELD_NUM: UByte = 0u
        public const val SAMPLE_TIME_OFFSET_FIELD_NUM: UByte = 1u
        public const val ACCEL_X_FIELD_NUM: UByte = 2u
        public const val ACCEL_Y_FIELD_NUM: UByte = 3u
        public const val ACCEL_Z_FIELD_NUM: UByte = 4u
        public const val CALIBRATED_ACCEL_X_FIELD_NUM: UByte = 5u
        public const val CALIBRATED_ACCEL_Y_FIELD_NUM: UByte = 6u
        public const val CALIBRATED_ACCEL_Z_FIELD_NUM: UByte = 7u
        public const val COMPRESSED_CALIBRATED_ACCEL_X_FIELD_NUM: UByte = 8u
        public const val COMPRESSED_CALIBRATED_ACCEL_Y_FIELD_NUM: UByte = 9u
        public const val COMPRESSED_CALIBRATED_ACCEL_Z_FIELD_NUM: UByte = 10u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("TimestampMs", 0u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[1] = Field("SampleTimeOffset", 1u, BaseType.UINT16, 1.0, 0.0, "ms", false)
            fields[2] = Field("AccelX", 2u, BaseType.UINT16, 1.0, 0.0, "counts", false)
            fields[3] = Field("AccelY", 3u, BaseType.UINT16, 1.0, 0.0, "counts", false)
            fields[4] = Field("AccelZ", 4u, BaseType.UINT16, 1.0, 0.0, "counts", false)
            fields[5] = Field("CalibratedAccelX", 5u, BaseType.FLOAT32, 1.0, 0.0, "g", false)
            fields[6] = Field("CalibratedAccelY", 6u, BaseType.FLOAT32, 1.0, 0.0, "g", false)
            fields[7] = Field("CalibratedAccelZ", 7u, BaseType.FLOAT32, 1.0, 0.0, "g", false)
            fields[8] = Field("CompressedCalibratedAccelX", 8u, BaseType.SINT16, 1.0, 0.0, "mG", false)
            fields[9] = Field("CompressedCalibratedAccelY", 9u, BaseType.SINT16, 1.0, 0.0, "mG", false)
            fields[10] = Field("CompressedCalibratedAccelZ", 10u, BaseType.SINT16, 1.0, 0.0, "mG", false)
            return fields
        }
    }
}
